/**
 * TicketTypesPanel — embeddable Tickets editor for the EventDashboard.
 *
 * Mirrors the internal-ticketing UI inside AdminEventManager but scoped
 * to a single event_id, so organizers can add / edit / delete ticket
 * types without leaving the unified Event Dashboard.
 *
 * Backend routes used (already auth-scoped to the event owner):
 *   GET    /api/events/:event_id/ticket-types
 *   POST   /api/events/:event_id/ticket-types
 *   PUT    /api/ticket-types/:tt_id
 *   DELETE /api/ticket-types/:tt_id
 *   GET    /api/events/:event_id/ticket-sales
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { DollarSign, Edit, Trash2, Save, Loader2, Plus, X, Receipt, Sparkles, Eye, EyeOff, Gift, ChevronUp, ChevronDown } from 'lucide-react';
import { Textarea } from './ui/textarea';
import { useConfirm } from './ConfirmDialog';
import TicketBadge from './TicketBadge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('eventism_token') || ''}` } });

const TIER_OPTIONS = [
  { value: 'general',    label: 'General Admission' },
  { value: 'vip',        label: 'VIP' },
  { value: 'early_bird', label: 'Early Bird' },
  { value: 'group',      label: 'Group' },
];

const VISIBILITY_OPTIONS = [
  { value: 'visible',              label: 'Visible',                 hint: 'Always shown on the public widget.' },
  { value: 'hidden',               label: 'Hidden',                  hint: 'Never shown publicly. Useful for invite-only or comp tiers.' },
  { value: 'hidden_until_on_sale', label: 'Hidden when not on sale', hint: 'Only visible during the sale window you set below.' },
  { value: 'members_only',         label: 'Members only',            hint: 'Only members of this event\'s brand can see + buy this tier.' },
];

const EMPTY_TT = {
  name: '', description: '', price: 0, quantity_total: 100,
  max_per_order: 10, min_per_order: 1, tier: 'general', perks: [], occurrence_id: '',
  manual_badge: null,
  visibility: 'visible',
  show_remaining: true,
  is_addon: false,
  variations: [],
  applies_to_ticket_types: [],
  sell_as_experience: false,
  experience_name: '',
  experience_description: '',
  experience_image_url: '',
  experience_tier_id: '',
};

const BADGE_COLORS = [
  { value: 'red',    label: 'Red',    cls: 'bg-red-500' },
  { value: 'orange', label: 'Orange', cls: 'bg-orange-500' },
  { value: 'gold',   label: 'Gold',   cls: 'bg-amber-500' },
  { value: 'green',  label: 'Green',  cls: 'bg-emerald-500' },
  { value: 'blue',   label: 'Blue',   cls: 'bg-blue-500' },
  { value: 'purple', label: 'Purple', cls: 'bg-purple-500' },
  { value: 'pink',   label: 'Pink',   cls: 'bg-pink-500' },
];

export default function TicketTypesPanel({ eventId, eventName }) {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [types, setTypes] = useState([]);
  const [sales, setSales] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_TT);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [t, s] = await Promise.all([
        // Owner-scoped fetch (with token) so hidden + hidden_until_on_sale
        // tiers still appear in the editor; the same endpoint filters
        // them out when called without auth from the public widget.
        axios.get(`${API}/events/${eventId}/ticket-types`, auth()),
        axios.get(`${API}/events/${eventId}/ticket-sales`, auth()).catch(() => ({ data: null })),
      ]);
      setTypes(t.data.ticket_types || []);
      setSales(s.data);
    } catch (err) {
      toast.error('Failed to load ticket types');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, [eventId]);

  const startCreate = () => {
    setEditingId(null);
    setForm(EMPTY_TT);
    setShowForm(true);
  };

  const startEdit = (tt) => {
    setEditingId(tt.ticket_type_id);
    setForm({
      name: tt.name || '',
      description: tt.description || '',
      price: tt.price || 0,
      quantity_total: tt.quantity_total || 0,
      max_per_order: tt.max_per_order || 10,
      min_per_order: tt.min_per_order || 1,
      tier: tt.tier || 'general',
      perks: tt.perks || [],
      occurrence_id: tt.occurrence_id || '',
      manual_badge: tt.manual_badge || null,
      visibility: tt.visibility || 'visible',
      show_remaining: tt.show_remaining !== false,
      is_addon: !!tt.is_addon,
      variations: tt.variations || [],
      applies_to_ticket_types: tt.applies_to_ticket_types || [],
      sell_as_experience: !!tt.sell_as_experience,
      experience_name: tt.experience_name || '',
      experience_description: tt.experience_description || '',
      experience_image_url: tt.experience_image_url || '',
      experience_tier_id: tt.experience_tier_id || '',
    });
    setShowForm(true);
  };

  const handleSave = async (e) => {
    e?.preventDefault?.();
    if (!form.name?.trim()) { toast.error('Name is required'); return; }
    if ((form.min_per_order || 1) > (form.max_per_order || 10)) {
      toast.error('Minimum per order cannot exceed maximum per order');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, occurrence_id: form.occurrence_id || null };
      if (editingId) {
        await axios.put(`${API}/ticket-types/${editingId}`, payload, auth());
        toast.success('Ticket type updated');
      } else {
        await axios.post(`${API}/events/${eventId}/ticket-types`, payload, auth());
        toast.success('Ticket type created');
      }
      setShowForm(false);
      setEditingId(null);
      setForm(EMPTY_TT);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (tt) => {
    const ok = await confirm({
      title: `Delete "${tt.name}"?`,
      description: 'This cannot be undone. If any tickets have been sold for this type the API will block the delete.',
      confirmText: 'Delete',
      destructive: true,
    });
    if (!ok) return;
    try {
      await axios.delete(`${API}/ticket-types/${tt.ticket_type_id}`, auth());
      toast.success('Ticket type deleted');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Cannot delete — tickets already sold');
    }
  };

  const moveRow = async (tt, dir) => {
    // Reorder within the tier's own group (admissions vs add-ons), then
    // persist the combined order so the public widget matches.
    const group = types.filter((t) => !!t.is_addon === !!tt.is_addon);
    const other = types.filter((t) => !!t.is_addon !== !!tt.is_addon);
    const idx = group.findIndex((t) => t.ticket_type_id === tt.ticket_type_id);
    const target = idx + dir;
    if (target < 0 || target >= group.length) return;
    const next = [...group];
    [next[idx], next[target]] = [next[target], next[idx]];
    const combined = tt.is_addon ? [...other, ...next] : [...next, ...other];
    setTypes(combined);
    try {
      await axios.put(`${API}/events/${eventId}/ticket-types/reorder`,
        { order: combined.map((t) => t.ticket_type_id) }, auth());
    } catch (err) {
      toast.error('Could not save order');
      fetchAll();
    }
  };

  const renderRow = (tt, idx, groupLen) => (
    <Card key={tt.ticket_type_id} className={`border-border ${tt.is_addon ? 'border-fuchsia-500/25' : ''}`} data-testid={`tt-row-${tt.ticket_type_id}`}>
      <CardContent className="p-4 flex items-center gap-3 flex-wrap">
        <div className="flex-1 min-w-[220px]">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-semibold">{tt.name}</p>
            {tt.is_addon ? (
              <Badge className="bg-fuchsia-500/15 text-fuchsia-300 border-fuchsia-500/30 text-[10px] gap-1" data-testid={`tt-row-addon-${tt.ticket_type_id}`}>
                <Gift className="h-2.5 w-2.5" /> Add-on
              </Badge>
            ) : (
              <Badge variant="outline" className="text-[10px] capitalize">{tt.tier || 'general'}</Badge>
            )}
            {!tt.is_active && <Badge className="bg-rose-500/15 text-rose-300 border-rose-500/30 text-[10px]">Inactive</Badge>}
            {tt.visibility === 'hidden' && (
              <Badge className="bg-zinc-700/40 text-zinc-300 border-zinc-600/40 text-[10px] gap-1" data-testid={`tt-row-hidden-${tt.ticket_type_id}`}>
                <EyeOff className="h-2.5 w-2.5" /> Hidden
              </Badge>
            )}
            {tt.visibility === 'hidden_until_on_sale' && (
              <Badge className="bg-indigo-500/15 text-indigo-300 border-indigo-500/30 text-[10px] gap-1" data-testid={`tt-row-window-${tt.ticket_type_id}`}>
                <Eye className="h-2.5 w-2.5" /> Window only
              </Badge>
            )}
            {tt.visibility === 'members_only' && (
              <Badge className="bg-amber-500/15 text-amber-300 border-amber-500/30 text-[10px] gap-1" data-testid={`tt-row-members-${tt.ticket_type_id}`}>
                Members only
              </Badge>
            )}
            {tt.show_remaining === false && (
              <Badge className="bg-amber-500/15 text-amber-300 border-amber-500/30 text-[10px]" data-testid={`tt-row-private-stock-${tt.ticket_type_id}`}>
                Stock hidden
              </Badge>
            )}
            {tt.manual_badge && <TicketBadge badge={tt.manual_badge} size="xs" testId={`tt-row-manual-${tt.ticket_type_id}`} />}
            {tt.auto_badge && <TicketBadge badge={tt.auto_badge} size="xs" testId={`tt-row-auto-${tt.ticket_type_id}`} />}
          </div>
          <p className="text-xs text-muted-foreground mt-0.5">
            {(tt.variations || []).length > 0 ? (
              <span className="font-semibold text-foreground">
                ${Math.min(...tt.variations.map(v => v.price)).toFixed(2)}–${Math.max(...tt.variations.map(v => v.price)).toFixed(2)}
              </span>
            ) : (
              <span className="font-semibold text-foreground">${Number(tt.price || 0).toFixed(2)}</span>
            )}
            {' · '}
            {(tt.quantity_sold || 0)} / {(tt.quantity_total || 0)} sold
            {' · '}
            {(tt.min_per_order || 1) === (tt.max_per_order || 10)
              ? `${tt.min_per_order || 1} / order`
              : `${tt.min_per_order || 1}–${tt.max_per_order || 10} / order`}
          </p>
          {(tt.variations || []).length > 0 && (
            <p className="text-xs text-fuchsia-300/80 mt-1" data-testid={`tt-row-variations-${tt.ticket_type_id}`}>
              {tt.variations.map(v => `${v.name} $${Number(v.price).toFixed(2)}`).join(' · ')}
            </p>
          )}
          {tt.description && <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{tt.description}</p>}
        </div>
        <div className="flex items-center gap-1">
          <div className="flex flex-col">
            <Button size="icon" variant="ghost" className="h-6 w-6" disabled={idx === 0} onClick={() => moveRow(tt, -1)} data-testid={`tt-move-up-${tt.ticket_type_id}`}>
              <ChevronUp className="h-4 w-4" />
            </Button>
            <Button size="icon" variant="ghost" className="h-6 w-6" disabled={idx === groupLen - 1} onClick={() => moveRow(tt, 1)} data-testid={`tt-move-down-${tt.ticket_type_id}`}>
              <ChevronDown className="h-4 w-4" />
            </Button>
          </div>
          <Button size="icon" variant="ghost" onClick={() => startEdit(tt)} data-testid={`tt-edit-${tt.ticket_type_id}`}>
            <Edit className="h-4 w-4" />
          </Button>
          <Button size="icon" variant="ghost" className="text-rose-300 hover:text-rose-200" onClick={() => handleDelete(tt)} data-testid={`tt-delete-${tt.ticket_type_id}`}>
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="py-10 flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-indigo-400" />
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="ticket-types-panel">
      {/* Live sales summary */}
      {sales && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="tickets-summary">
          <Stat label="Tickets sold" value={sales.total_tickets_sold} color="text-indigo-300" />
          <Stat label="Revenue" value={`$${(sales.total_revenue || 0).toLocaleString()}`} color="text-emerald-300" />
          <Stat label="Capacity" value={sales.total_capacity} color="text-amber-300" />
          <Stat label="Orders" value={sales.orders_count} color="text-blue-300" />
        </div>
      )}

      {/* Header row */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="font-bold flex items-center gap-2">
          <Receipt className="h-4 w-4 text-indigo-300" /> Ticket types ({types.filter(t => !t.is_addon).length})
        </h3>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => navigate(`/admin/events/${eventId}/transactions`)}
            data-testid="tt-open-transactions"
            className="border-emerald-500/40 text-emerald-300 hover:border-emerald-400"
          >
            <DollarSign className="h-3.5 w-3.5 mr-1.5" /> Transactions
          </Button>
          <Button
            size="sm"
            onClick={startCreate}
            data-testid="tt-add-new"
            className="bg-gradient-to-r from-indigo-500 to-fuchsia-500 hover:from-indigo-400 hover:to-fuchsia-400 text-white"
          >
            <Plus className="h-3.5 w-3.5 mr-1.5" /> Add ticket type
          </Button>
        </div>
      </div>

      {/* List */}
      {types.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="p-8 text-center">
            <Receipt className="h-10 w-10 text-muted-foreground/40 mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">No ticket types yet.</p>
            <p className="text-xs text-muted-foreground mt-1">Add your first one to start selling for <span className="font-semibold">{eventName}</span>.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2" data-testid="tt-list">
          {types.filter(t => !t.is_addon).map((tt, i, arr) => renderRow(tt, i, arr.length))}
          {types.some(t => t.is_addon) && (
            <>
              <div className="flex items-center gap-2 pt-3" data-testid="addons-section-header">
                <Gift className="h-4 w-4 text-fuchsia-400" />
                <h3 className="font-bold text-sm">Add-ons ({types.filter(t => t.is_addon).length})</h3>
                <span className="text-[11px] text-muted-foreground">Don't count toward capacity · require a ticket at checkout</span>
              </div>
              {types.filter(t => t.is_addon).map((tt, i, arr) => renderRow(tt, i, arr.length))}
            </>
          )}
        </div>
      )}

      {/* Add / Edit form */}
      {showForm && (
        <Card className="border-indigo-500/30 bg-indigo-500/5">
          <CardContent className="p-5 space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="font-semibold">{editingId ? 'Edit ticket type' : 'Add ticket type'}</h4>
              <Button size="icon" variant="ghost" onClick={() => { setShowForm(false); setEditingId(null); setForm(EMPTY_TT); }} data-testid="tt-form-close">
                <X className="h-4 w-4" />
              </Button>
            </div>
            <form onSubmit={handleSave} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5 sm:col-span-2">
                <Label className="text-xs">Name *</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. General Admission"
                  required
                  data-testid="tt-form-name"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Tier</Label>
                <select
                  className="w-full h-10 px-3 border rounded-md bg-background text-sm"
                  value={form.tier}
                  onChange={(e) => setForm({ ...form, tier: e.target.value })}
                  data-testid="tt-form-tier"
                >
                  {TIER_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Price (USD)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.price}
                  onChange={(e) => setForm({ ...form, price: parseFloat(e.target.value) || 0 })}
                  data-testid="tt-form-price"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Total quantity</Label>
                <Input
                  type="number"
                  min="0"
                  value={form.quantity_total}
                  onChange={(e) => setForm({ ...form, quantity_total: parseInt(e.target.value, 10) || 0 })}
                  data-testid="tt-form-qty"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Min per order</Label>
                <Input
                  type="number"
                  min="1"
                  value={form.min_per_order}
                  onChange={(e) => setForm({ ...form, min_per_order: Math.max(1, parseInt(e.target.value, 10) || 1) })}
                  data-testid="tt-form-min"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Max per order</Label>
                <Input
                  type="number"
                  min="1"
                  value={form.max_per_order}
                  onChange={(e) => setForm({ ...form, max_per_order: parseInt(e.target.value, 10) || 1 })}
                  data-testid="tt-form-max"
                />
              </div>
              <div className="sm:col-span-2 flex items-center justify-between gap-3 rounded-md border border-fuchsia-500/25 bg-fuchsia-500/5 px-3 py-2.5">
                <div>
                  <Label className="text-xs font-medium flex items-center gap-1.5" htmlFor="tt-form-is-addon">
                    <Gift className="h-3.5 w-3.5 text-fuchsia-400" /> This is an add-on
                  </Label>
                  <p className="text-[11px] text-muted-foreground">
                    Non-admission item like Open Bar, Parking or Merch. Sold alongside tickets (requires ≥1 ticket in the order), doesn't count toward event capacity.
                  </p>
                </div>
                <Switch
                  id="tt-form-is-addon"
                  checked={!!form.is_addon}
                  onCheckedChange={(v) => setForm({ ...form, is_addon: !!v, ...(v ? {} : { variations: [] }) })}
                  data-testid="tt-form-is-addon"
                />
              </div>

              {form.is_addon && (() => {
                const admissionTiers = types.filter((t) => !t.is_addon && t.is_active !== false);
                const selected = form.applies_to_ticket_types || [];
                const chip = (on) => `px-2.5 py-1 rounded-full border text-[11px] font-medium transition-colors ${on ? 'border-fuchsia-400 bg-fuchsia-500/20 text-fuchsia-200' : 'border-border text-muted-foreground hover:border-fuchsia-500/40'}`;
                const toggleTier = (id) => setForm((f) => {
                  const cur = f.applies_to_ticket_types || [];
                  return { ...f, applies_to_ticket_types: cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id] };
                });
                return (
                  <div className="sm:col-span-2 space-y-1.5 rounded-lg border border-fuchsia-500/20 bg-fuchsia-500/5 p-3" data-testid="tt-form-applies-to">
                    <Label className="text-xs font-semibold">Which tickets can get this upgrade?</Label>
                    <p className="text-[10px] text-muted-foreground">
                      Buyers can only add this upgrade when their order includes an eligible ticket. Leave on "All ticket types" to offer it with everything.
                    </p>
                    <div className="flex flex-wrap gap-1.5 pt-1">
                      <button type="button" className={chip(selected.length === 0)} onClick={() => setForm((f) => ({ ...f, applies_to_ticket_types: [] }))} data-testid="tt-form-applies-all">
                        All ticket types
                      </button>
                      {admissionTiers.map((t) => (
                        <button type="button" key={t.ticket_type_id} className={chip(selected.includes(t.ticket_type_id))} onClick={() => toggleTier(t.ticket_type_id)} data-testid={`tt-form-applies-${t.ticket_type_id}`}>
                          {t.name}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })()}

              {form.is_addon && (() => {
                const admissionTiers = types.filter((t) => !t.is_addon && t.is_active !== false);
                const pairedTier = admissionTiers.find((t) => t.ticket_type_id === form.experience_tier_id) || admissionTiers[0];
                const expPrice = ((pairedTier?.price || 0) + (form.price || 0)).toFixed(2);
                return (
                  <div className="sm:col-span-2 space-y-2 rounded-lg border border-amber-500/25 bg-amber-500/5 p-3" data-testid="tt-form-experience">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <Label className="text-xs font-semibold flex items-center gap-1.5" htmlFor="tt-form-sell-exp">
                          <Sparkles className="h-3.5 w-3.5 text-amber-400" /> Also sell as an experience
                        </Label>
                        <p className="text-[10px] text-muted-foreground">
                          Lists this ticket + add-on as one package in the venue's Experiences area and Eventism discovery. Buying it creates a normal ticket + add-on sale — both inventories decrement, no separate stock.
                        </p>
                      </div>
                      <Switch
                        id="tt-form-sell-exp"
                        checked={!!form.sell_as_experience}
                        onCheckedChange={(v) => setForm((f) => ({
                          ...f,
                          sell_as_experience: !!v,
                          experience_name: f.experience_name || (v ? `${f.name || 'Add-on'} Experience at ${eventName || 'this event'}` : f.experience_name),
                          experience_tier_id: f.experience_tier_id || (v ? (admissionTiers[0]?.ticket_type_id || '') : f.experience_tier_id),
                        }))}
                        data-testid="tt-form-sell-exp-switch"
                      />
                    </div>
                    {form.sell_as_experience && (
                      <div className="space-y-2 pt-1">
                        <div className="space-y-1">
                          <Label className="text-[11px]">Experience name</Label>
                          <Input value={form.experience_name} onChange={(e) => setForm({ ...form, experience_name: e.target.value })} data-testid="tt-form-exp-name" />
                        </div>
                        <div className="grid sm:grid-cols-2 gap-2">
                          <div className="space-y-1">
                            <Label className="text-[11px]">Paired ticket tier</Label>
                            <select
                              className="w-full h-9 px-2 border rounded-md bg-background text-sm"
                              value={pairedTier?.ticket_type_id || ''}
                              onChange={(e) => setForm({ ...form, experience_tier_id: e.target.value })}
                              data-testid="tt-form-exp-tier"
                            >
                              {admissionTiers.map((t) => <option key={t.ticket_type_id} value={t.ticket_type_id}>{t.name} (${t.price})</option>)}
                            </select>
                          </div>
                          <div className="space-y-1">
                            <Label className="text-[11px]">Experience price (auto)</Label>
                            <div className="h-9 px-3 flex items-center rounded-md border border-border bg-muted/50 text-sm font-semibold" data-testid="tt-form-exp-price">
                              ${expPrice} <span className="text-[10px] text-muted-foreground font-normal ml-2">= ${pairedTier?.price ?? 0} ticket + ${form.price || 0} add-on</span>
                            </div>
                          </div>
                        </div>
                        <div className="space-y-1">
                          <Label className="text-[11px]">Experience description</Label>
                          <Textarea rows={2} value={form.experience_description} placeholder="Defaults to the add-on's description" onChange={(e) => setForm({ ...form, experience_description: e.target.value })} data-testid="tt-form-exp-desc" />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-[11px]">Photo URL (defaults to the event's cover image)</Label>
                          <Input value={form.experience_image_url} placeholder="https://..." onChange={(e) => setForm({ ...form, experience_image_url: e.target.value })} data-testid="tt-form-exp-image" />
                        </div>
                        <p className="text-[10px] text-muted-foreground">Times default to the event's date &amp; time. Unchecking this removes the experience from discovery — past orders are untouched.</p>
                      </div>
                    )}
                  </div>
                );
              })()}

              {form.is_addon && (
                <div className="sm:col-span-2 space-y-2 rounded-lg border border-fuchsia-500/20 bg-fuchsia-500/5 p-3" data-testid="tt-form-variations">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div>
                      <Label className="text-xs font-semibold">Variations (optional)</Label>
                      <p className="text-[10px] text-muted-foreground">
                        For items with options — e.g. T-shirt sizes. Each variation has its own price; quantity is shared. Leave empty for a simple add-on like Open Bar.
                      </p>
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="h-7 text-xs border-fuchsia-500/40 text-fuchsia-300"
                      onClick={() => setForm((f) => ({ ...f, variations: [...(f.variations || []), { id: `var_${Math.random().toString(36).slice(2, 10)}`, name: '', price: 0 }] }))}
                      data-testid="tt-form-add-variation"
                    >
                      <Plus className="h-3 w-3 mr-1" /> Add variation
                    </Button>
                  </div>
                  {(form.variations || []).map((v, idx) => (
                    <div key={v.id || idx} className="flex items-center gap-2" data-testid={`tt-form-variation-${idx}`}>
                      <Input
                        value={v.name}
                        onChange={(e) => setForm((f) => ({ ...f, variations: f.variations.map((x, i) => i === idx ? { ...x, name: e.target.value } : x) }))}
                        placeholder="e.g. Small / Medium / Large"
                        className="flex-1"
                        data-testid={`tt-form-variation-name-${idx}`}
                      />
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={v.price}
                        onChange={(e) => setForm((f) => ({ ...f, variations: f.variations.map((x, i) => i === idx ? { ...x, price: parseFloat(e.target.value) || 0 } : x) }))}
                        className="w-28"
                        data-testid={`tt-form-variation-price-${idx}`}
                      />
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8 text-rose-300"
                        onClick={() => setForm((f) => ({ ...f, variations: f.variations.filter((_, i) => i !== idx) }))}
                        data-testid={`tt-form-variation-remove-${idx}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  ))}
                  {(form.variations || []).length > 0 && (
                    <p className="text-[10px] text-muted-foreground">With variations set, buyers pick one and pay the variation's price (the base price above is ignored).</p>
                  )}
                </div>
              )}

              <div className="space-y-1.5 sm:col-span-2">
                <Label className="text-xs">Visibility</Label>
                <select
                  className="w-full h-10 px-3 border rounded-md bg-background text-sm"
                  value={form.visibility}
                  onChange={(e) => setForm({ ...form, visibility: e.target.value })}
                  data-testid="tt-form-visibility"
                >
                  {VISIBILITY_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
                <p className="text-[11px] text-muted-foreground">
                  {VISIBILITY_OPTIONS.find((o) => o.value === form.visibility)?.hint}
                </p>
              </div>
              <div className="sm:col-span-2 flex items-center justify-between gap-3 rounded-md border border-border bg-muted/20 px-3 py-2.5">
                <div>
                  <Label className="text-xs font-medium" htmlFor="tt-form-show-remaining">
                    Show &ldquo;tickets remaining&rdquo; on the public widget
                  </Label>
                  <p className="text-[11px] text-muted-foreground">
                    Hides the &ldquo;X left&rdquo; counter and scarcity badges that leak inventory levels. &ldquo;Sold Out&rdquo; still appears.
                  </p>
                </div>
                <Switch
                  id="tt-form-show-remaining"
                  checked={!!form.show_remaining}
                  onCheckedChange={(v) => setForm({ ...form, show_remaining: !!v })}
                  data-testid="tt-form-show-remaining"
                />
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label className="text-xs">Description (optional)</Label>
                <Input
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="What does this ticket include?"
                  data-testid="tt-form-description"
                />
              </div>

              {/* Manual badge editor — organizer-set "Most Popular" /
                  "Best Value" / custom — overlays the auto badge that's
                  computed live (scarcity / velocity / etc.). */}
              <div className="sm:col-span-2 space-y-2 rounded-lg border border-amber-500/20 bg-amber-500/5 p-3">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <Label className="text-xs font-semibold flex items-center gap-1.5">
                      <Sparkles className="h-3.5 w-3.5 text-amber-300" /> Spotlight badge
                    </Label>
                    <p className="text-[10px] text-muted-foreground">
                      Curated label shown on the public ticket widget — e.g. &ldquo;Most Popular&rdquo;, &ldquo;Best Value&rdquo;.
                    </p>
                  </div>
                  {form.manual_badge && (
                    <TicketBadge badge={form.manual_badge} size="sm" />
                  )}
                </div>
                <div className="grid sm:grid-cols-[1fr_auto] gap-2">
                  <Input
                    value={form.manual_badge?.text || ''}
                    onChange={(e) => {
                      const text = e.target.value;
                      setForm((f) => ({
                        ...f,
                        manual_badge: text
                          ? { text, color: f.manual_badge?.color || 'gold' }
                          : null,
                      }));
                    }}
                    placeholder="e.g. Most Popular, Best Value, VIP Access…"
                    maxLength={32}
                    data-testid="tt-form-badge-text"
                  />
                  <div className="flex items-center gap-1 flex-wrap" data-testid="tt-form-badge-colors">
                    {BADGE_COLORS.map((c) => {
                      const active = (form.manual_badge?.color || 'gold') === c.value;
                      return (
                        <button
                          key={c.value}
                          type="button"
                          title={c.label}
                          onClick={() => setForm((f) => ({
                            ...f,
                            manual_badge: f.manual_badge?.text
                              ? { ...f.manual_badge, color: c.value }
                              : { text: 'Most Popular', color: c.value },
                          }))}
                          className={`h-6 w-6 rounded-full ${c.cls} transition-all ${active ? 'ring-2 ring-offset-2 ring-offset-background ring-white scale-110' : 'opacity-70 hover:opacity-100'}`}
                          data-testid={`tt-form-badge-color-${c.value}`}
                        />
                      );
                    })}
                  </div>
                </div>
                {form.manual_badge && (
                  <button
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, manual_badge: null }))}
                    className="text-[10px] uppercase tracking-wider text-rose-300 hover:text-rose-200"
                    data-testid="tt-form-badge-clear"
                  >
                    Clear badge
                  </button>
                )}
              </div>

              <div className="sm:col-span-2 flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => { setShowForm(false); setEditingId(null); setForm(EMPTY_TT); }} data-testid="tt-form-cancel">
                  Cancel
                </Button>
                <Button type="submit" disabled={saving} data-testid="tt-form-save">
                  {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />}
                  {editingId ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function Stat({ label, value, color = 'text-indigo-300' }) {
  return (
    <Card className="border-border">
      <CardContent className="p-3 text-center">
        <p className={`text-2xl font-bold tabular-nums ${color}`}>{value}</p>
        <p className="text-[10px] uppercase tracking-wider text-muted-foreground mt-0.5">{label}</p>
      </CardContent>
    </Card>
  );
}
